import { Actions, Button, Dialog, Stack, Text } from 'braid-design-system';
import React from 'react';

import { validateEvent } from '#shared/validateEvent';
import { presignUpload } from '#src/api/communityDirectory';
import { createEvent } from '#src/api/events';
import { getAccessToken } from '#src/utils/cognitoAuth';
import { resizeImageFile, uploadToPresignedPost } from '#src/utils/imageResize';

import { DialogErrorNotice } from '../DialogErrorNotice';
import { EventFields } from './EventFields';
import {
  emptyEventFieldValues,
  toEventInput,
  type EventFieldValues,
} from './eventFieldUpdates';

type CreateEventDialogProps = {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  onError: (message: string | null) => void;
};

export const CreateEventDialog = ({
  open,
  onClose,
  onCreated,
  onError,
}: CreateEventDialogProps) => {
  const [fields, setFields] = React.useState<EventFieldValues>(
    emptyEventFieldValues,
  );
  const [imageFile, setImageFile] = React.useState<File | null>(null);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setFields(emptyEventFieldValues());
      setImageFile(null);
      setErrors({});
    }
  }, [open]);

  const handleFieldChange = (
    field: Exclude<keyof EventFieldValues, 'dates'>,
    value: string | boolean,
  ) => {
    setFields((current) => ({ ...current, [field]: value }));
  };

  const handleDatesChange = (dates: string[]) => {
    setFields((current) => ({ ...current, dates }));
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  const handleCreate = async () => {
    const input = toEventInput(fields);
    const validation = validateEvent(input, { requireDescription: false });
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    const token = getAccessToken();
    if (!token) {
      setErrors({ _form: 'Session expired. Please log in again.' });
      return;
    }

    setErrors({});
    setSubmitting(true);
    onError(null);

    try {
      let imageKey: string | undefined;
      if (imageFile) {
        const { blob, contentType } = await resizeImageFile(imageFile);
        const presigned = await presignUpload(contentType);
        await uploadToPresignedPost(
          presigned.url,
          presigned.fields,
          blob,
          contentType,
        );
        imageKey = presigned.key;
      }

      await createEvent(token, toEventInput(fields, imageKey));
      onCreated();
      onClose();
    } catch (err) {
      if (err && typeof err === 'object' && 'errors' in err) {
        setErrors((err as { errors: Record<string, string> }).errors);
      } else if (err && typeof err === 'object' && 'error' in err) {
        setErrors({ _form: String((err as { error: string }).error) });
      } else {
        setErrors({
          _form: err instanceof Error ? err.message : 'Something went wrong.',
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      id="create-event"
      title="Create event"
      open={open}
      onClose={handleClose}
      width="medium"
    >
      <Stack space="medium">
        <DialogErrorNotice message={errors._form} />

        <EventFields
          eventId="new"
          fields={fields}
          errors={errors}
          onChange={handleFieldChange}
          onDatesChange={handleDatesChange}
        />

        <Stack space="small">
          <Text weight="strong">Event image (optional)</Text>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => setImageFile(e.currentTarget.files?.[0] ?? null)}
          />
          <Text tone="secondary">JPEG, PNG or WebP, up to 5 MB</Text>
        </Stack>

        <Actions>
          <Button tone="formAccent" loading={submitting} onClick={handleCreate}>
            Create
          </Button>
          <Button variant="transparent" onClick={handleClose}>
            Cancel
          </Button>
        </Actions>
      </Stack>
    </Dialog>
  );
};
